import React from "react";
import {Button, notification} from "antd";
import {t} from "i18next";

/**
 * Deploy sonrası açık duran sekmelerin eski JS ile çalışmaya devam etmesini önler.
 *
 * Sunucu tarafı (Program.cs) index.html'i cache'lemeyerek YENİ açılışlarda taze shell verir; ancak aylarca
 * açık kalan bir sekme kendiliğinden yenilenmez. Bu modül:
 *   1) asset-manifest.json'ı (içerik-hash'li entrypoints) periyodik + sekmeye dönüşte kontrol eder; değişmişse
 *      kullanıcıya "Yeni sürüm var, yenile" bildirimi gösterir.
 *   2) Silinmiş bir chunk yüklenememesi (ChunkLoadError) durumunda tek seferlik otomatik reload yapar.
 */

const MANIFEST_URL = "/asset-manifest.json";
const POLL_INTERVAL = 3 * 60 * 1000; // 3 dk
const RELOAD_GUARD_MS = 10 * 1000;   // chunk hatasında 10 sn içinde tekrar reload etme

let baseline = null;     // ilk başarılı kontroldeki sürüm kimliği
let promptShown = false; // bildirim bir kez gösterilsin

async function fetchVersion() {
    try {
        // Cache-busting + no-store: her zaman sunucudaki güncel manifesti al.
        const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {cache: "no-store"});
        if (!res.ok) return null;
        const data = await res.json();
        // entrypoints = içerik-hash'li ana js/css listesi → sürüm parmak izi.
        if (Array.isArray(data.entrypoints) && data.entrypoints.length) {
            return data.entrypoints.join("|");
        }
        return data.files?.["main.js"] ?? null;
    } catch {
        return null;
    }
}

function showUpdatePrompt() {
    if (promptShown) return;
    promptShown = true;

    notification.open({
        key: "app-update",
        message: t("New version available"),
        description: t("A new version of the application is available. Please refresh to update."),
        duration: 0, // kalıcı; kullanıcı kapatana/yenileyene kadar durur
        placement: "bottomRight",
        btn: (
            <Button type="primary" size="small" onClick={() => window.location.reload()}>
                {t("Refresh")}
            </Button>
        ),
    });
}

async function check() {
    const current = await fetchVersion();
    if (!current) return;

    if (baseline == null) {
        baseline = current; // bu sekmenin çalıştığı sürümü referans al
        return;
    }
    if (current !== baseline) {
        showUpdatePrompt();
    }
}

function installChunkErrorReload() {
    const isChunkError = (value) => {
        const msg = typeof value === "string" ? value : (value?.message || value?.name || "");
        return /Loading chunk [\w]+ failed/i.test(msg)
            || /Loading CSS chunk/i.test(msg)
            || /ChunkLoadError/i.test(msg);
    };

    const reloadOnce = () => {
        // Zaman damgalı koruma: kalıcı arıza durumunda sonsuz reload döngüsüne girme.
        const last = Number(sessionStorage.getItem("chunk-reload-ts") || 0);
        if (Date.now() - last < RELOAD_GUARD_MS) return;
        sessionStorage.setItem("chunk-reload-ts", String(Date.now()));
        window.location.reload();
    };

    window.addEventListener("error", (e) => {
        if (isChunkError(e?.error) || isChunkError(e?.message)) reloadOnce();
    });
    window.addEventListener("unhandledrejection", (e) => {
        if (isChunkError(e?.reason)) reloadOnce();
    });
}

let started = false;

/** index.js'ten render sonrası bir kez çağrılır. */
export function startVersionWatch() {
    if (started) return;
    started = true;

    check(); // baseline'ı kur
    setInterval(check, POLL_INTERVAL);

    // Kullanıcı sekmeye geri döndüğünde anında kontrol (en sık yakalama noktası).
    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") check();
    });

    installChunkErrorReload();
}
